import { isAccountFacebookPage, isInstagramAccount } from "@/entities/accounts";
import { User } from "features/user";
import type { AccountType } from "shared/types/accounts";
import { globalUserPreferencesToggles } from "utils/accounts";
import { hasEntitlement } from "utils/has-entitlement";
import { isReverseTrial } from "utils/reverse-trial";

type AdsPermissionUser =
  | (Partial<Pick<User, "plan">> & {
      [key: string]: unknown;
      role?: string;
      subscription?: {
        subscription?: {
          status?: string;
        };
      };
      preferences?: {
        [key: string]: unknown;
        canBoostPosts?: boolean;
        entitlements?: Record<string, string | undefined>;
      };
      canBoostPosts?: boolean;
    })
  | null
  | undefined;

export function isAdsFeatureOn(account: AccountType, user: AdsPermissionUser) {
  if (!isAccountFacebookPage(account)) return false;
  // Reverse trial → every feature on.
  if (isReverseTrial(user)) return true;
  // Entitlement-first: if the backend grants the meta-ads entitlement the
  // feature is on regardless of plan code name.
  if (hasEntitlement(user, "meta-ads")) return true;
  // Legacy plan-code fallback for non-migrated users.
  return !!user?.plan?.includes("sked-enterprise");
}

export function isAdsPermissionRequired(
  account: AccountType,
  user: AdsPermissionUser
) {
  const scope = "ads_read";
  return (
    isAdsFeatureOn(account, user) && !account.session?.scopes?.includes(scope)
  );
}

export function getRequiredAdsPermissionAccounts(
  accounts: AccountType[] | undefined,
  accountsToUpdate:
    { facebook?: AccountType[]; instagram?: AccountType[] } | undefined,
  user: AdsPermissionUser
) {
  return (accounts || []).filter((acc) => {
    if (accountsToUpdate?.facebook?.some((a) => a._id === acc._id)) {
      return false;
    }
    return isAdsPermissionRequired(acc, user);
  });
}

/**
 * Whether a user on a given plan can access collaboration. Kept for callers
 * that only have the plan string on hand (legacy callers). Prefer
 * `hasTeamwork` from `utils/feature-gates` which also considers entitlements
 * and reverse-trial state.
 */
export function isCollaborationEnabledForPlan(plan: string) {
  return [
    "sked-professional",
    "sked-enterprise",
    "sked-custom",
    "sked-essentials",
  ].some((c) => (plan || "").includes(c));
}

export function canBoostPost(
  account: AccountType,
  user: AdsPermissionUser
): boolean {
  const isMeta = isAccountFacebookPage(account) || isInstagramAccount(account);
  const toggleOn = globalUserPreferencesToggles.enableBoostPosts === true;
  // Collaborator boost access is primarily persisted in preferences, but some
  // payloads may still expose the legacy flattened field.
  const hasPermission =
    user?.role !== "collaborator" ||
    user.preferences?.canBoostPosts === true ||
    user?.canBoostPosts === true;
  return isMeta && toggleOn && hasPermission;
}
